import React from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowRight, Sparkles, Crosshair, Megaphone, MessageSquare, Gift, Footprints,
  Award, Activity, Radio, Monitor, Smartphone, ThumbsUp, Tv, Layout, FileText,
  Search, Users, Repeat, TrendingUp, BarChart3, Database, Zap, Eye, MapPin,
  RefreshCw, Target, Store, LineChart, Layers, Coins, Check
} from "lucide-react";
import { FootTrafficAuditForm } from "../components/FootTrafficAuditForm";
import { CaseStudies } from "../components/CaseStudies";
import { Testimonials } from "../components/Testimonials";
import { SEO } from "../components/SEO";

const SmartReachPage = () => {
  const navigate = useNavigate();

  return (
    <div className="pt-20" data-testid="smartreach-page">
      <SEO
        title="SmartReach™ Platform"
        description="FunAsia SmartReach™ — proprietary platform combining digital advertising, radio, hyperlocal targeting, and dual loyalty rewards to drive real customer visits and measurable revenue."
        path="/smartreach"
      />

      {/* Hero */}
      <section className="section-padding bg-gradient-to-br from-orange-light via-white to-white">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-4 bg-white px-4 py-2 rounded-full shadow-sm">
            <Sparkles size={16} /> Proprietary Technology
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-gray-dark mb-6 leading-tight">
            FunAsia <span className="text-gradient-orange">SmartReach&trade;</span> Platform
          </h1>
          <p className="text-lg md:text-xl text-gray-medium mb-8 max-w-3xl mx-auto">
            The unified marketing system that combines media, data, and rewards to drive real customer
            visits — turning advertising spend into measurable foot traffic and revenue growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate("/contact")} className="btn-primary text-lg" data-testid="smartreach-cta-top">
              Launch SmartReach Campaign <ArrowRight size={20} className="inline ml-2" />
            </button>
            <button onClick={() => navigate("/contact")} className="btn-secondary text-lg">
              Book a Demo
            </button>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="section-padding bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-2">
              <span className="inline-flex items-center gap-2 text-orange-primary font-semibold text-sm mb-3 bg-orange-light px-3 py-1 rounded-full">
                Overview
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
                A Unified System Built For <span className="text-gradient-orange">Real-World Outcomes</span>
              </h2>
            </div>
            <div className="lg:col-span-3 space-y-4">
              <p className="text-lg text-gray-dark leading-relaxed">
                SmartReach&trade; is not a single ad product — it's an outcome engine. We unify the channels
                your customers actually use (radio, digital, mobile, social, DOOH) with the data that
                makes them effective (location intelligence, behavior signals, foot-traffic insights),
                and tie it all together with a rewards system that gives them a reason to come back.
              </p>
              <p className="text-gray-medium leading-relaxed">
                The result: campaigns that don't just generate impressions — they generate visits,
                conversions, and repeat customers you can measure on your dashboard and feel in your revenue.
              </p>
              <div className="grid grid-cols-3 gap-4 pt-4">
                {[
                  { label: "Media", icon: Megaphone },
                  { label: "Data", icon: Database },
                  { label: "Rewards", icon: Award }
                ].map((p) => (
                  <div key={p.label} className="bg-gray-light rounded-xl p-4 text-center">
                    <p.icon className="text-orange-primary mx-auto mb-2" size={28} />
                    <p className="font-display font-bold text-gray-dark text-sm">{p.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CORE CAPABILITIES */}
      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              Core <span className="text-gradient-orange">Capabilities</span>
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">
              Five capabilities working in concert to drive customers through your door.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Crosshair, title: "Hyperlocal Targeting",
                desc: "Define audiences by precise radius, foot-traffic patterns, competitor proximity, and behavioral signals — so your spend reaches people who can actually walk in.",
                bullets: ["Radius & polygon targeting", "Competitor conquesting", "Visit-history audiences"]
              },
              {
                icon: Megaphone, title: "Omnichannel Activation",
                desc: "Reach the same customer across radio, mobile, social, CTV, and DOOH with consistent messaging — the way they live their day.",
                bullets: ["Cross-channel frequency", "Unified creative system", "Daypart orchestration"]
              },
              {
                icon: Award, title: "Loyalty-Driven Engagement",
                desc: "Dual points across radio, digital, and store visits create a reason for customers to come back — not just convert once.",
                bullets: ["Earn-and-redeem mechanics", "Tier upgrades", "Partner reward network"]
              },
              {
                icon: Activity, title: "Foot Traffic Analytics",
                desc: "Measure visits, dwell time, repeat rate, and visit lift attributed to each campaign so you know what's actually working.",
                bullets: ["Visit attribution", "Lift studies", "Location benchmarking"]
              },
              {
                icon: RefreshCw, title: "Real-Time Optimization",
                desc: "Daily signals feed an optimization loop that shifts budget toward the channels, creatives, and audiences driving real visits.",
                bullets: ["Auto-budget allocation", "Creative rotation", "Audience refinement"]
              },
              {
                icon: Eye, title: "Transparent Reporting",
                desc: "One dashboard with the metrics that matter to your business — not just impressions and CTR.",
                bullets: ["Visit lift", "Cost per visit", "Revenue attribution (where available)"]
              }
            ].map((cap) => (
              <div key={cap.title} className="bg-white p-6 rounded-2xl shadow-card border border-gray-100 card-hover" data-testid={`capability-card-${cap.title.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="w-12 h-12 rounded-xl bg-orange-light flex items-center justify-center mb-4">
                  <cap.icon className="text-orange-primary" size={24} />
                </div>
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{cap.title}</h3>
                <p className="text-gray-medium text-sm mb-4">{cap.desc}</p>
                <ul className="space-y-2 pt-3 border-t border-gray-100">
                  {cap.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2 text-sm text-gray-dark">
                      <Check size={14} className="text-orange-primary flex-shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CHANNEL INTEGRATION */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              Channel Integration: <span className="text-gradient-orange">One Platform, Every Touchpoint</span>
            </h2>
            <p className="text-gray-medium max-w-3xl mx-auto text-lg">
              SmartReach&trade; combines traditional and digital channels into a single orchestrated journey —
              so customers see your message in the moments that matter most.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
            {[
              { icon: Radio, label: "Radio", note: "FunAsia network" },
              { icon: Monitor, label: "Digital Display" },
              { icon: Smartphone, label: "Mobile" },
              { icon: ThumbsUp, label: "Social" },
              { icon: Tv, label: "Connected TV" },
              { icon: Layout, label: "DOOH" }
            ].map((c) => (
              <div key={c.label} className="bg-gray-light p-5 rounded-2xl text-center card-hover">
                <c.icon className="text-orange-primary mx-auto mb-2" size={32} />
                <p className="font-display font-semibold text-gray-dark text-sm">{c.label}</p>
                {c.note && <p className="text-xs text-gray-medium mt-1">{c.note}</p>}
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-orange-light to-white rounded-3xl p-8 border border-orange-primary/10">
            <h3 className="font-display text-xl font-bold text-gray-dark mb-3">How the channels work together</h3>
            <p className="text-gray-medium mb-4">
              Morning radio creates awareness during the commute. Mobile geofencing serves an offer when the
              customer is near your location. A DOOH billboard reinforces the brand. Social retargeting closes
              the loop in the evening. Loyalty rewards bring them back next week. <span className="text-orange-primary font-semibold">One audience, one message, every channel.</span>
            </p>
          </div>
        </div>
      </section>

      {/* CUSTOMER JOURNEY */}
      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              The SmartReach&trade; <span className="text-gradient-orange">Customer Journey</span>
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">
              From the first impression to the fifth visit — one unified flow.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 md:gap-4">
            {[
              { icon: Eye, num: "01", title: "Awareness", desc: "Reach the right local audience across radio + digital." },
              { icon: MessageSquare, num: "02", title: "Engagement", desc: "Time-based offers and locally relevant messaging." },
              { icon: Footprints, num: "03", title: "Visit", desc: "Customers walk through your door — measured." },
              { icon: Gift, num: "04", title: "Loyalty", desc: "Earn points across radio, digital, and store." },
              { icon: Repeat, num: "05", title: "Repeat Visit", desc: "Reward redemptions bring them back again." }
            ].map((s) => (
              <div key={s.num} className="relative bg-white p-5 rounded-2xl shadow-card border border-gray-100 card-hover" data-testid={`journey-step-${s.num}`}>
                <span className="absolute top-3 right-3 text-xs font-display font-bold text-orange-primary/30">{s.num}</span>
                <div className="w-11 h-11 rounded-lg gradient-orange flex items-center justify-center mb-3">
                  <s.icon className="text-white" size={22} />
                </div>
                <h3 className="font-display font-bold text-base text-gray-dark mb-1">{s.title}</h3>
                <p className="text-gray-medium text-xs">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <CaseStudies title="SmartReach™ in Action" />

      {/* BUSINESS IMPACT */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              The <span className="text-gradient-orange">Business Impact</span>
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto text-lg">
              Outcomes you can put on a P&L — not a slide.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: TrendingUp, label: "Higher ROI", desc: "Outcome-based budgeting shifts spend toward channels driving real visits — not vanity clicks. Most clients see double-digit lift in efficiency within 90 days." },
              { icon: Repeat, label: "Repeat Customers", desc: "Loyalty rewards turn one-time visits into repeat habits. Higher CLV and lower acquisition cost per customer over time." },
              { icon: LineChart, label: "Measurable Growth", desc: "Visit lift, attribution, and revenue tie advertising directly to business growth — proving what's working and where to double down." }
            ].map((b, idx) => (
              <div key={idx} className="bg-gradient-to-br from-orange-light to-white p-8 rounded-2xl border border-orange-primary/10 card-hover">
                <b.icon className="text-orange-primary mb-4" size={40} />
                <h3 className="font-display font-bold text-2xl text-gray-dark mb-3">{b.label}</h3>
                <p className="text-gray-medium">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASE BLOCKS */}
      <section className="section-padding bg-gray-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-dark mb-4">
              Common Use Cases
            </h2>
            <p className="text-gray-medium max-w-2xl mx-auto">
              Built for the moments where local marketing actually moves the needle.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: "Drive Lunch Traffic", desc: "Daypart-targeted radio + mobile push within 3 miles, 11am–2pm.", icon: Store },
              { title: "Increase Weekend Visits", desc: "Friday-Sunday loyalty point boosts paired with hyperlocal social.", icon: TrendingUp },
              { title: "Launch a New Store", desc: "Geo-radius DOOH + radio + retargeting for first 30 days.", icon: Megaphone }
            ].map((u) => (
              <div key={u.title} className="bg-white p-6 rounded-2xl shadow-card card-hover" data-testid={`use-case-${u.title.replace(/\s+/g, "-").toLowerCase()}`}>
                <u.icon className="text-orange-primary mb-3" size={32} />
                <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{u.title}</h3>
                <p className="text-gray-medium text-sm">{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CROSS LINKS */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { title: "How It Works", desc: "Step-by-step process from audience to visit.", path: "/how-it-works", icon: Layers },
            { title: "Foot Traffic Marketing", desc: "Drive measurable in-store visits.", path: "/foot-traffic-marketing", icon: Footprints },
            { title: "Loyalty Platform", desc: "Dual rewards that drive repeat visits.", path: "/loyalty-platform", icon: Award }
          ].map((c) => (
            <Link key={c.path} to={c.path} className="bg-gradient-to-br from-orange-light to-white p-6 rounded-2xl border border-orange-primary/10 card-hover block">
              <c.icon className="text-orange-primary mb-3" size={32} />
              <h3 className="font-display font-bold text-lg text-gray-dark mb-2">{c.title}</h3>
              <p className="text-gray-medium text-sm mb-3">{c.desc}</p>
              <span className="text-orange-primary font-semibold text-sm inline-flex items-center gap-1">
                Learn more <ArrowRight size={14} />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* AUDIT FORM */}
      <FootTrafficAuditForm
        title="See How SmartReach™ Could Drive Customers To Your Door"
        subtitle="Tell us about your business. We'll send back a custom plan in 24 hours — built for your locations, audience, and goals."
        variant="dark"
      />

      {/* TESTIMONIALS */}
      <Testimonials
        title="Why Businesses Switch to SmartReach™"
        subtitle="Real owners. Real results. Real customers walking through their doors."
        background="bg-white"
      />

      {/* CTA */}
      <section className="section-padding gradient-orange">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to launch a SmartReach&trade; campaign?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Get a custom plan built for your business. No commitment required.
          </p>
          <button onClick={() => navigate("/contact")} className="bg-white text-orange-primary px-8 py-4 rounded-full font-semibold text-lg hover:shadow-lg transition-all duration-300 hover:-translate-y-1" data-testid="smartreach-cta-bottom">
            Launch SmartReach Campaign <ArrowRight size={20} className="inline ml-2" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default SmartReachPage;
